import { InputControlProps } from '../input.control.props';
import { CloudUpload, Delete } from '@mui/icons-material';
import React, { DragEvent, useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControl, FormHelperText, FormLabel, Icon, Stack, styled, Typography } from '@mui/material';
import { FileDto } from '../../../../model/dto/common';
import { useValidation } from '../../../../hooks/UseValidation';
import ValidationContext from '../../../../hooks/UseValidation/validation.context';
import { useSnackbar } from 'notistack';
import { formatFileSize } from '../../../../utils';
import { useFileIcon } from '../../../../hooks/UseFileIcon';

interface IProps extends InputControlProps<FileDto[]> {
  accept?: string;
  limit?: number;
  maxSize?: number;
  value: FileDto[];
}

const StyledLabel = styled('label')({
    cursor: 'pointer',
    textAlign: 'center',
    display: 'flex',
    '&:hover p,&:hover svg,& img': {
      opacity: 1,
    },
    '& p, svg': {
      opacity: 0.4,
    },
    '&:hover img': {
      opacity: 0.3,
    },
});

export function FileControl({ limit = 1, accept, maxSize = 5242880, ...props }: IProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const validate = useValidation(props.validators);
  const { validateOn } = useContext(ValidationContext);
  const [errorText, setErrorText] = useState<string>();
  const [labelText, setLabelText] = useState(t('common:file.control.hoverText'));
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const stopDefaults = (e: DragEvent<HTMLElement>) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const updateFileList = async (newFiles: FileList) => {
    const dtos = props.value;
    for (let i = 0; i < limit - props.value.length; i++) {
      const file = newFiles.item(i);
      if (file === null) break;

      if (file.size > maxSize) {
        enqueueSnackbar(
          t('common:file.control.tooLargeFile', [t(...formatFileSize(maxSize))]),
          { variant: 'error' },
        );
        continue;
      }

      const dto = await FileDto.fromBlob(file);
      if (accept === undefined || accept.includes(dto.type)) {
        dtos.push(dto);
      }
    }
    if (inputRef.current) {
      inputRef.current.files = new DataTransfer().files;
    }
    props.onChange(dtos);
  };

  const dragEvents = useMemo(() => ({
    onDragEnter: (e: DragEvent<HTMLElement>) => {
      stopDefaults(e)
      setIsDragOver(true)
      setLabelText(t('common:file.control.dropLabel'))
    },
    onDragLeave: (e: DragEvent<HTMLElement>) => {
      stopDefaults(e)
      setIsDragOver(false)
      setLabelText(t('common:file.control.hoverText'))
    },
    onDragOver: stopDefaults,
    onDrop: (e: DragEvent<HTMLElement>) => {
      stopDefaults(e)
      setLabelText(t('common:file.control.hoverText'))
      setIsDragOver(false)
      return updateFileList(e.dataTransfer.files);
    },
  }), []);

  const getIcon = useFileIcon();

  const handleRemoveFile = useCallback((files: FileDto[], idx: number) => {
    files.splice(idx, 1);
    props.onChange(files);
  }, []);

  useEffect(() => {
    setErrorText(validate(props.value, props.tab ?? 'main'));
  }, [props.value, validateOn]);

  return (
    <FormControl fullWidth required={props.required} error={!!errorText} size="small" margin="dense">
      <FormLabel>{t(props.labelKey)}</FormLabel>
      <Stack direction="column">
        {props.value.map((file, idx) => <Stack key={idx} direction="row">
          <Icon sx={{mr: 1}}>{getIcon(file.type)}</Icon>
          <span>{file.name}</span>
          <Delete sx={{ ml: 1, cursor: 'pointer' }} color="error" onClick={() => handleRemoveFile(props.value, idx)}/>
        </Stack>)}
      </Stack>
      <input onChange={e => updateFileList(e.target.files ?? new FileList())} ref={inputRef} accept={accept} style={{display: 'none'}} max={limit} multiple={limit > 1} id="file-upload" type="file" />
      {limit > props.value.length && (<StyledLabel
        htmlFor="file-upload"
        {...dragEvents}
        sx={isDragOver ? {
          '& img': {
            opacity: 0.3,
          },
          '& p, svg': {
            opacity: 1,
          },
        } : undefined
      }>
        <Box width={600} height={100} sx={{ pointerEvents: 'none' }}>
            <Box width={600} height={100}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'absolute',
              }}
            >
              <CloudUpload fontSize="large" />
              <Typography>{labelText}</Typography>
            </Box>
        </Box>
      </StyledLabel>)}
      <FormHelperText>{errorText}</FormHelperText>
    </FormControl>
  )
}